<!--
  Search modal rendered at body-bottom to escape the navbar's
  backdrop-filter stacking context, which breaks position:fixed.
-->

<let/isOpen=false/>
<let/query=""/>
<let/results=[]/>
<let/loading=false/>
<let/selectedIndex=0/>
<let/searchTimeout=undefined/>

<lifecycle onMount() {
  if (typeof window !== 'undefined') {
    import('markopress/search').then(mod => {
      window.__markopress_search = mod.search;
    });

    window.addEventListener('search:toggle', (e) => {
      const detail = (e as CustomEvent).detail;
      if (detail.open) {
        isOpen = true;
        requestAnimationFrame(() => {
          const input = document.querySelector('.search-input') as HTMLInputElement;
          input?.focus();
        });
      } else {
        isOpen = false;
        query = "";
        results = [];
      }
    });
  }
}/>

<if=isOpen>
  <div
    class="search-modal"
    onClick(e) {
      if (e.target === e.currentTarget) {
        isOpen = false;
        query = "";
        results = [];
        window.dispatchEvent(new CustomEvent('search:closed'));
      }
    }>
    <div class="search-modal-content">
      <div class="search-input-wrapper">
        <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input
          type="text"
          class="search-input"
          placeholder="Search documentation..."
          value=query
          autofocus=true
          onKeyDown(e) {
            if (e.key === 'Escape') {
              isOpen = false;
              query = "";
              results = [];
              window.dispatchEvent(new CustomEvent('search:closed'));
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              selectedIndex = Math.min(selectedIndex + 1, results.length - 1);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              selectedIndex = Math.max(selectedIndex - 1, 0);
            } else if (e.key === 'Enter' && results[selectedIndex]) {
              window.location.href = results[selectedIndex].url;
              isOpen = false;
              query = "";
              results = [];
              window.dispatchEvent(new CustomEvent('search:closed'));
            }
          }
          onInput(e) {
            const newQuery = e.target.value;
            query = newQuery;

            if (!newQuery.trim()) {
              results = [];
              return;
            }

            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(async () => {
              loading = true;
              try {
                const runSearch = window.__markopress_search;
                if (runSearch) {
                  results = await runSearch(newQuery);
                  selectedIndex = 0;
                }
              } catch (error) {
                console.error('Search failed:', error);
                results = [];
              }
              loading = false;
            }, 150);
          }
        />
        <kbd class="search-shortcut-small">ESC</kbd>
      </div>

      <if=loading>
        <div class="search-loading">
          <span>Searching...</span>
        </div>
      </if>
      <else if=results.length>
        <ul class="search-results">
          <for|result, index| of=results>
            <li class={ 'search-result-item': true, 'selected': index === selectedIndex }>
              <a
                href=result.url
                onClick() {
                  isOpen = false;
                  query = "";
                  results = [];
                  window.dispatchEvent(new CustomEvent('search:closed'));
                }>
                <span class="result-title">${result.title}</span>
                <if=result.titles?.length>
                  <span class="result-breadcrumb">
                    ${result.titles.join(' → ')}
                  </span>
                </if>
              </a>
            </li>
          </for>
        </ul>
      </else>
      <else if=query && !loading>
        <div class="search-empty">
          <span>No results for "${query}"</span>
        </div>
      </else>
      <else>
        <div class="search-placeholder">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" class="search-placeholder-icon">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <p class="search-placeholder-text">Type to search</p>
          <p class="search-placeholder-hint">Press <kbd>ESC</kbd> to close</p>
        </div>
      </else>
    </div>
  </div>
</if>

<style>
  .search-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: calc(var(--navbar-height, 64px) + var(--space-xl));
    z-index: 100;
    animation: fade-in 0.2s ease-out;
  }

  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .search-modal-content {
    width: 90%;
    max-width: 640px;
    max-height: 70vh;
    background: var(--bg-default);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg, 12px);
    box-shadow: var(--shadow-5);
    overflow: hidden;
    animation: slide-down 0.2s ease-out;
  }

  @keyframes slide-down {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .search-input-wrapper {
    display: flex;
    align-items: center;
    gap: var(--space-md);
    padding: var(--space-md) var(--space-lg);
    border-bottom: 1px solid var(--border-default);
  }

  .search-icon {
    flex-shrink: 0;
    color: var(--text-3);
  }

  .search-input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: var(--font-size-base);
    color: var(--text-1);
    outline: none;
  }

  .search-input::placeholder {
    color: var(--text-3);
  }

  .search-shortcut-small {
    padding: 2px 6px;
    font-size: 11px;
    font-family: var(--font-mono);
    background: var(--bg-default);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-sm, 4px);
    color: var(--text-3);
    flex-shrink: 0;
  }

  .search-loading,
  .search-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-xl);
    color: var(--text-3);
  }

  .search-results {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 400px;
    overflow-y: auto;
  }

  .search-result-item {
    border-bottom: 1px solid var(--border-default);
  }

  .search-result-item:last-child {
    border-bottom: none;
  }

  .search-result-item a {
    display: block;
    padding: var(--space-md) var(--space-lg);
    text-decoration: none;
    color: var(--text-1);
    transition: background var(--transition-fast);
  }

  .search-result-item a:hover,
  .search-result-item.selected a {
    background: var(--bg-soft);
  }

  .result-title {
    display: block;
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--space-xs);
  }

  .result-breadcrumb {
    display: block;
    font-size: var(--font-size-sm);
    color: var(--text-3);
  }

  .search-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-3xl);
    text-align: center;
  }

  .search-placeholder-icon {
    margin-bottom: var(--space-md);
    color: var(--text-3);
    opacity: 0.5;
  }

  .search-placeholder-text {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-medium);
    color: var(--text-1);
    margin-bottom: var(--space-sm);
  }

  .search-placeholder-hint {
    font-size: var(--font-size-sm);
    color: var(--text-3);
  }

  .search-placeholder-hint kbd {
    padding: 2px 6px;
    font-size: 11px;
    font-family: var(--font-mono);
    background: var(--bg-alt);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-sm, 4px);
  }
</style>
